import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import backgroundImage from "../assets/bg3.jpg";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const HeroSection = styled.section`
  height: 100vh;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    align-items: flex-start;
    padding-top: 180px;
    background-attachment: scroll;
  }

  @media (max-width: 576px) {
    padding-top: 160px;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const HeroTitle = styled.h1`
  font-size: 4.5rem;
  margin-bottom: 25px;
  font-weight: 800;
  letter-spacing: 2px;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.2),
    0 8px 16px rgba(0, 0, 0, 0.1);
  animation: ${float} 6s ease-in-out infinite;

  @media (max-width: 768px) {
    font-size: 3.2rem;
    margin-bottom: 20px;
  }

  @media (max-width: 576px) {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 2rem;
  margin-bottom: 50px;
  font-weight: 300;
  line-height: 1.6;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.3);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 35px;
  }

  @media (max-width: 576px) {
    font-size: 1.3rem;
    margin-bottom: 30px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 25px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 18px 45px;
  background: ${(props) =>
    props.outline
      ? "transparent"
      : "linear-gradient(135deg, #1a5f7a, #159895)"};
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.2rem;
  transition: all 0.4s ease;
  border: 2px solid ${(props) => (props.outline ? "white" : "transparent")};
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    background: ${(props) =>
      props.outline
        ? "rgba(255, 255, 255, 0.1)"
        : "linear-gradient(135deg, #159895, #1a5f7a)"};
  }

  @media (max-width: 576px) {
    width: 85%;
    text-align: center;
    padding: 15px 30px;
    font-size: 1.1rem;
  }
`;

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <HeroSection id="home">
      <HeroContent>
        <HeroTitle>Welcome to Skyline Terrace</HeroTitle>
        <HeroSubtitle>
          Experience luxury living in the heart of Galiyat, where modern comfort
          meets breathtaking natural beauty
        </HeroSubtitle>
        <ButtonContainer>
          <Button to="/join">Join Our Community</Button>
          <Button to="/apartments" outline="true">
            Explore Apartments
          </Button>
        </ButtonContainer>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero;
